import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * NouvelleVersionBanner — dit à l'utilisateur que le portail servi n'est plus celui de sa page.
 *
 * Registre #20266, fiche 93ad31f2 : MaintenanceBanner ne s'arme que sur des erreurs API, or un
 * déploiement « frontend » ne touche pas l'API — l'interface est remplacée sous les pieds de
 * l'utilisateur sans aucun signe. La version servie est déjà publiée (/build-info.json, no-store) :
 * on la lit au montage, puis on la relit à intervalle espacé. Deux états distincts, deux bandeaux :
 * « service injoignable » reste à MaintenanceBanner, ici seule « version changée » est mesurée.
 *
 * La sonde s'arrête dès le changement constaté (un GET léger et espacé en fonctionnement normal,
 * rien ensuite). Le libellé décrit l'état constaté, jamais une cause supposée (constat 52).
 * Limite : un déploiement backend seul ne change pas /build-info.json (servi par le conteneur web).
 */
export const INTERVALLE_SONDE_MS = 30 * 1000;

const INCONNUES = new Set(['', 'unknown', 'pending']);

async function lireCommit() {
  try {
    const r = await fetch('/build-info.json?t=' + Date.now(), { cache: 'no-store' });
    if (!r.ok) return null;
    const commit = (await r.json())?.commit;
    return typeof commit === 'string' && !INCONNUES.has(commit) ? commit : null;
  } catch {
    return null;
  }
}

function depuis(sec) {
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
}

export default function NouvelleVersionBanner({ recharger = () => window.location.reload() }) {
  const [constateA, setConstateA] = useState(null);
  const [ecoule, setEcoule] = useState(0);
  const depart = useRef(null);

  useEffect(() => {
    let vivant = true;
    let timer = null;
    const sonder = async () => {
      const commit = await lireCommit();
      if (!vivant || !commit) return;
      if (depart.current === null) { depart.current = commit; return; }
      if (commit !== depart.current) {
        clearInterval(timer);
        setConstateA(Date.now());
      }
    };
    sonder().then(() => {
      // Version de départ illisible (build local) : rien à comparer, aucune sonde.
      if (vivant && depart.current !== null) timer = setInterval(sonder, INTERVALLE_SONDE_MS);
    });
    return () => { vivant = false; clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (!constateA) return undefined;
    const t = setInterval(() => setEcoule(Math.floor((Date.now() - constateA) / 1000)), 1000);
    return () => clearInterval(t);
  }, [constateA]);

  if (!constateA) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
        background: 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 100%)', color: '#fff',
        padding: '10px 20px', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '0.9rem', fontWeight: 600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <RefreshCw size={16} aria-hidden="true" />
      <span>
        Une nouvelle version du portail est disponible
        {ecoule > 5 && <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.85 }}>(depuis {depuis(ecoule)})</span>}
      </span>
      <button
        type="button"
        onClick={recharger}
        style={{
          background: '#fff', color: '#1d4ed8', border: 'none', borderRadius: 6, padding: '4px 12px',
          fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem',
        }}
      >
        Recharger la page
      </button>
    </div>
  );
}
